/**
 * pages/AdminAudit.tsx — the audit log for the business unit this window is on.
 *
 * The audit log is the one screen that is read by somebody who already believes something went wrong, so
 * it is built for the two things they will do: narrow, and then point somebody else at exactly one row.
 *
 * **It is scoped, and that is not a filter this screen applies.** `GET /api/admin/audit` binds the caller's
 * named business unit through `requireScopeAccess` before it reads anything; the section grant says the
 * caller may read *an* audit log and Gate 3 says *whose*. Nothing here re-filters by business unit,
 * because a client-side scope filter is exactly the arrangement where the data has already left the
 * database by the time the check happens.
 *
 * **Filtering is server-side where the server offers it, and client-side only where it does not.** Action,
 * outcome and the time bounds are query parameters, so narrowing actually narrows the query rather than
 * hiding rows that were fetched anyway. The actor box is the exception: the route filters by actor *id*
 * and a human types an email address, so that one match happens here — and it is honest about only
 * matching what was loaded.
 *
 * **Deep links live in the query string, not the hash.** The hash is the router's; putting a row id there
 * would either collide with a route or invent one. `?entry=<id>` (with the current filters alongside it)
 * survives being pasted into a ticket, reproduces the same view, and marks the row it names — and if that
 * row is not in the result, the page says so rather than silently showing a different one.
 *
 * **It says how much history it is showing, in every state.** See `RetentionNote` below: a log that states
 * nothing about its own extent is read as a complete history, and this one stated nothing until 7 August
 * 2026 (`06-modules.md` §3.2).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ApiError, api, type AuditEntry, type AuditListResponse } from "../lib/api";
import { formatDateTime, formatRelative, humanise, truncate } from "../lib/format";
import {
  Badge,
  Banner,
  Button,
  DeniedState,
  ErrorState,
  LoadingState,
  Page,
  Select,
  Table,
  TextField,
  type Column,
  type Tone,
} from "../components/ui";

export interface AuditFilters {
  readonly action: string;
  readonly outcome: string;
  readonly actor: string;
  readonly since: string;
  readonly until: string;
}

const EMPTY_FILTERS: AuditFilters = { action: "", outcome: "", actor: "", since: "", until: "" };

/**
 * How many entries this screen asks for, and the number the reader is told about.
 *
 * A constant rather than a literal in two places: the sentence in `RetentionNote` is only honest while it
 * matches the query, and a figure typed into prose beside a figure typed into a query string is a pair
 * that drifts on the first tuning change and drifts silently — the screen goes on claiming a window size
 * it no longer asks for.
 *
 * The server clamps any limit to 500 (`AuditQuerySchema` in `server/routes/admin.ts`, and `listAudit`
 * again in `server/db/audit.ts`), so this is a page size the route will honour, not a request it will
 * quietly reduce.
 */
export const AUDIT_PAGE_SIZE = 200;

export interface AdminAuditPorts {
  readonly load: (filters: AuditFilters, signal: AbortSignal) => Promise<AuditListResponse>;
}

const apiPorts: AdminAuditPorts = {
  load: (filters, signal) =>
    api.get<AuditListResponse>("/api/admin/audit", {
      query: {
        limit: AUDIT_PAGE_SIZE,
        ...(filters.action === "" ? {} : { action: filters.action }),
        ...(filters.outcome === "" ? {} : { outcome: filters.outcome }),
        ...(filters.since === "" ? {} : { since: filters.since }),
        ...(filters.until === "" ? {} : { until: filters.until }),
      },
      signal,
    }),
};

const OUTCOME_TONE: Readonly<Record<string, Tone>> = {
  allowed: "success",
  refused: "warning",
  error: "danger",
};

/** The filters as they arrived in the URL, so a pasted link reproduces the view it was taken from. */
export function filtersFromSearch(search: string): AuditFilters {
  const params = new URLSearchParams(search);
  return {
    action: params.get("action") ?? "",
    outcome: params.get("outcome") ?? "",
    actor: params.get("actor") ?? "",
    since: params.get("since") ?? "",
    until: params.get("until") ?? "",
  };
}

/** The reverse: a URL that names one row and carries the filters that were in force when it was taken. */
export function permalinkFor(entryId: string, filters: AuditFilters, location: Location): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== "") params.set(key, value);
  }
  params.set("entry", entryId);
  return `${location.pathname}?${params.toString()}${location.hash}`;
}

/** The `detail` column is arbitrary JSON from whichever route wrote the row; it is shown, never parsed. */
function describeDetail(detail: unknown): string {
  if (detail === null || detail === undefined) return "";
  if (typeof detail === "string") return detail;
  try {
    return JSON.stringify(detail);
  } catch {
    return "";
  }
}

/**
 * How much history this screen is showing — stated where a reader meets it before the rows, never under
 * them.
 *
 * `06-modules.md` §3.2: *"The retention window MUST be stated on the screen. A log silently truncated at
 * ninety days is read as a complete history."* Until 7 August 2026 this page said nothing anywhere about
 * retention or extent, so a reader who could not find the act they came for had to decide for themselves
 * whether it had never happened, or had merely aged out of a window nobody had told them about. Those two
 * answers lead to opposite investigations.
 *
 * Both halves of the sentence are facts about **this** repository, verified in the code rather than
 * assumed from a policy somebody once wrote down — and no figure appears in it that is not in the source:
 *
 *  - **Nothing purges either trail.** `ticket_audit` (`migrations/0006_audit.sql`) and
 *    `group_policy_audit` (`migrations/0003_permissions.sql`) are append-only: `server/db/audit.ts` holds
 *    one INSERT and three SELECTs and no UPDATE or DELETE, and there is no age-based sweep of either table
 *    anywhere in the server. The one process that wakes on a timer — `runTick` in `server/engine/cli.ts`,
 *    which is exactly where this repository's CLAUDE.md hazard 8 says housekeeping lives — sweeps expired
 *    sessions and sign-in transactions and nothing else. "Kept indefinitely" **is** a retention window,
 *    and saying so is what the clause asks for: it tells the reader that an entry's absence is a fact
 *    about what was recorded, not about how long ago it happened.
 *  - **What is on screen is a window over that store.** `GET /api/admin/audit` bounds the **row count**,
 *    never the period: this page asks for `AUDIT_PAGE_SIZE` newest-first rows. A page size is not a
 *    retention window, but it is the other way a screen shows less than the store holds, and
 *    `04-data.md` §1.9 is emphatic that a window must never present itself as the whole. The
 *    *"showing N of M"* banner further down states when the cap actually bit; this states that the cap
 *    exists at all, which is the part a reader needs **before** concluding anything from a short list.
 *
 * The one removal path in the schema is deliberately not on the screen: `ticket_audit.bu_id` is
 * `REFERENCES business_units (id) ON DELETE CASCADE` and foreign keys are on, so deleting a business unit
 * would take its entries with it. `deleteBusinessUnit` has no caller anywhere in the server — no route
 * reaches it — and a unit that no longer exists has no audit screen to mislead anybody with. Stating it
 * here rather than in the sentence keeps the sentence about what a reader can act on; it is written down
 * so that whoever finds the cascade later can see it was weighed and not missed.
 *
 * It renders in **every** state — denied and empty included, and in the same position in each. Those are
 * precisely the states where an absence of rows is misread as an absence of events, and a notice that
 * moves about between states is a notice the reader stops looking for.
 *
 * Deliberately quiet: `--text-xs` on `--text-muted`, this stylesheet's settled idiom for a line that must
 * be present without competing with the data (`.login__footnote`, `.field__hint`, `.account__version`).
 */
function RetentionNote(): JSX.Element {
  return (
    <p className="audit-retention">
      Entries are kept indefinitely — this log is append-only, and nothing removes an entry with age. This
      view loads at most the {AUDIT_PAGE_SIZE} most recent entries matching the filters, so anything older
      is reached by narrowing the dates rather than by scrolling.
    </p>
  );
}

export interface AdminAuditProps {
  readonly buId: string;
  readonly canView: boolean;
  readonly ports?: AdminAuditPorts;
  /** Injected by tests; production reads the real one. */
  readonly location?: Location;
}

export function AdminAudit({ buId, canView, ports = apiPorts, location }: AdminAuditProps): JSX.Element {
  const here = location ?? window.location;
  const [filters, setFilters] = useState<AuditFilters>(() => filtersFromSearch(here.search));
  const [linked] = useState<string | null>(() => new URLSearchParams(here.search).get("entry"));
  const [data, setData] = useState<AuditListResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  const reload = useCallback((): void => setAttempt((current) => current + 1), []);

  // The query key: the server-side filters only, since the actor box narrows what is already in hand and
  // re-fetching on every keystroke would be a request per character for no new rows.
  const key = `${buId}|${filters.action}|${filters.outcome}|${filters.since}|${filters.until}|${attempt}`;

  useEffect(() => {
    if (!canView) return;
    const controller = new AbortController();
    let live = true;
    setData(null);
    setError(null);

    ports
      .load(filters, controller.signal)
      .then((loaded) => {
        if (live) setData(loaded);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null));
      });

    return () => {
      live = false;
      controller.abort();
    };
    // `filters` is re-created on every keystroke; `key` is what says the QUERY actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ports, canView, key]);

  const entries = useMemo(() => {
    const rows = data?.entries ?? [];
    const needle = filters.actor.trim().toLowerCase();
    if (needle === "") return rows;
    return rows.filter(
      (entry) =>
        (entry.actorEmail ?? "").toLowerCase().includes(needle) ||
        (entry.actorId ?? "").toLowerCase().includes(needle),
    );
  }, [data, filters.actor]);

  if (!canView) {
    return (
      <Page title="Audit">
        {/*
          The same notice, in the same place, as the page a permitted reader gets. Somebody refused here
          will be told by a colleague what the log does or does not hold, and a screen that answers that
          only for the people who can already see the rows is a screen that answers it to nobody who asks.
        */}
        <RetentionNote />
        <DeniedState body="Reading the audit log is its own grant, and it is scoped: it shows this business unit's entries only." />
      </Page>
    );
  }

  const linkedMissing = linked !== null && data !== null && !entries.some((entry) => entry.id === linked);

  const columns: readonly Column<AuditEntry>[] = [
    {
      key: "at",
      header: "When",
      type: "date",
      value: (entry) => entry.at,
      render: (entry) => (
        <span className="row">
          <time title={formatDateTime(entry.at)}>{formatRelative(entry.at)}</time>
          {entry.id === linked ? <Badge tone="info">Linked</Badge> : null}
        </span>
      ),
    },
    {
      key: "actorEmail",
      header: "Who",
      type: "text",
      value: (entry) => entry.actorEmail ?? entry.actorId,
      render: (entry) => (
        <span className="mono">
          {entry.actorEmail ?? entry.actorId ?? "—"}
          {entry.actorKind === "user" ? "" : ` (${humanise(entry.actorKind)})`}
        </span>
      ),
    },
    { key: "action", header: "Action", type: "text", value: (entry) => entry.action },
    {
      key: "outcome",
      header: "Outcome",
      type: "text",
      value: (entry) => entry.outcome,
      render: (entry) => <Badge tone={OUTCOME_TONE[entry.outcome] ?? "neutral"}>{humanise(entry.outcome)}</Badge>,
    },
    {
      key: "buId",
      header: "Business unit",
      type: "text",
      // Constant across the whole table by construction — every row is this scope's. Shown anyway, because
      // a screenshot of an audit row with no tenant on it is a screenshot that proves nothing.
      value: (entry) => entry.buId,
    },
    {
      key: "subject",
      header: "Subject",
      type: "text",
      value: (entry) => (entry.subjectKind === null ? entry.subjectId : `${entry.subjectKind} ${entry.subjectId ?? ""}`),
      hideOnMobile: true,
    },
    {
      key: "detail",
      header: "Detail",
      type: "text",
      value: (entry) => describeDetail(entry.detail),
      render: (entry) => {
        const text = describeDetail(entry.detail);
        return text === "" ? "—" : <span className="mono" title={text}>{truncate(text, 60)}</span>;
      },
      hideOnMobile: true,
    },
    {
      key: "traceId",
      header: "Trace",
      type: "text",
      value: (entry) => entry.traceId,
      render: (entry) => <span className="mono">{entry.traceId ?? "—"}</span>,
    },
    {
      key: "link",
      header: "Link",
      sortable: false,
      render: (entry) => (
        <a href={permalinkFor(entry.id, filters, here)} title={`Permanent link to entry ${entry.id}`}>
          Link
        </a>
      ),
    },
  ];

  const actionOptions = [
    { value: "", label: "Every action" },
    ...(data?.actions ?? []).map((action) => ({ value: action, label: action })),
  ];

  return (
    <Page
      title="Audit"
      lede="Every write is recorded against the business unit it happened in — allowed and refused alike. A refusal is evidence, so it is kept exactly as carefully as a success."
    >
      {/*
        First child of the page body, so the extent of the log is met before the filters and long before
        the rows. Below the table it would be a footnote, and a footnote arrives after the reader has
        already drawn their conclusion from what was — or was not — in front of them.
      */}
      <RetentionNote />

      <div className="row">
        <Select
          label="Action"
          value={filters.action}
          options={actionOptions}
          onChange={(action) => setFilters((current) => ({ ...current, action }))}
        />
        <Select
          label="Outcome"
          value={filters.outcome}
          options={[
            { value: "", label: "Every outcome" },
            { value: "allowed", label: "Allowed" },
            { value: "refused", label: "Refused" },
            { value: "error", label: "Error" },
          ]}
          onChange={(outcome) => setFilters((current) => ({ ...current, outcome }))}
        />
        <TextField
          label="Actor"
          value={filters.actor}
          onChange={(actor) => setFilters((current) => ({ ...current, actor }))}
          type="search"
          placeholder="Email or id"
          hint="Matches the entries already loaded, not the whole log."
        />
        <TextField
          label="From"
          value={filters.since}
          onChange={(since) => setFilters((current) => ({ ...current, since }))}
          placeholder="2026-03-01"
          hint="ISO date or timestamp."
        />
        <TextField
          label="To"
          value={filters.until}
          onChange={(until) => setFilters((current) => ({ ...current, until }))}
          placeholder="2026-03-31"
        />
        <Button variant="ghost" onClick={() => setFilters(EMPTY_FILTERS)}>
          Clear filters
        </Button>
      </div>

      {linkedMissing ? (
        <Banner tone="warning">
          The entry this link names is not in the result below. It may belong to another business unit, or
          it may fall outside the filters carried by the link. Clear the filters and try again.
        </Banner>
      ) : null}

      {data !== null && data.total > data.entries.length ? (
        <Banner tone="info">
          Showing the {data.entries.length} most recent of {data.total} matching entries. Narrow the dates
          to reach further back.
        </Banner>
      ) : null}

      {error !== null ? (
        error.isPermissionDenied ? (
          <DeniedState body={error.userMessage} />
        ) : (
          <ErrorState message={error.userMessage} traceId={error.traceId} onRetry={reload} />
        )
      ) : null}

      {error === null && data === null ? <LoadingState label="Loading the audit log" /> : null}

      {data !== null ? (
        <Table
          caption="Audit entries"
          columns={columns}
          rows={entries}
          rowKey={(entry) => entry.id}
          defaultSort={{ key: "at", direction: "descending" }}
          titleKey="action"
          mobile="cards"
          empty={{
            title: "Nothing matches",
            body: "Entries appear as soon as somebody creates, changes or publishes an update in this business unit. If you expected one, widen the dates or clear the action filter.",
            action: (
              <Button variant="secondary" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            ),
          }}
        />
      ) : null}
    </Page>
  );
}
